<template>
  <div>
    <v-btn v-if="!hideButton" :elevation="0" @click="open">
      {{ $t("create") }}
    </v-btn>

    <confirm-action-dialog
      v-if="dialogModel"
      width="1050"
      :persistent="true"
      :supressKeyboardActions="true"
      :title="$t('person.createPerson')"
      :isDialogActive.sync="dialogModel"
      :rightText="$t('create')"
      :loading="isLoading"
      :fullscreen="fullscreen"
      :rightLoading="isLoading"
      :rightDisabled="!isFormValid"
      @leftClick="close"
      @close="close"
      @rightClick="onRightClick"
    >
      <v-form v-model="isFormValid">
        <v-row class="pt-3">
          <v-col cols="12" md="7">
            <v-text-field
              v-model="person.firstName"
              :label="$t('objects.person.firstName')"
              ref="personNameField"
              outlined
            />
            <v-text-field v-model="person.lastName" :label="$t('objects.person.lastName')" outlined />
            <v-text-field
              v-model="person.externalId"
              :label="$t('objects.person.externalId')"
              :hint="$t('objects.invoiceLine.externalIdHint')"
              outlined
              clearable
            />
            <markdown-editor v-model="person.description" :label="$t('objects.person.description')" />
          </v-col>
          <v-col cols="12" md="5">
            <div v-for="(email, index) in person.emails" :key="index">
              <v-subheader>
                {{ $t("common.nouns.email") }} #{{ index + 1 }}
                <v-spacer />
                <v-btn text x-small @click="person.emails.splice(index, 1)">
                  {{ $t("person.removeEmail") }}
                </v-btn>
              </v-subheader>
              <v-text-field
                v-model="email.email"
                :label="$t('objects.person.emails.email') + ' *'"
                :rules="emailRule"
                outlined
              />
              <v-autocomplete
                v-model="email.label"
                :items="Object.values(PersonLabelEnum)"
                :label="$t('objects.person.emails.label')"
                auto-select-first
                outlined
              >
                <template #item="{ item }">
                  {{ $t(`enums.PersonLabelEnum.${item}`) }}
                </template>
                <template #selection="{ item }">
                  {{ $t(`enums.PersonLabelEnum.${item}`) }}
                </template>
              </v-autocomplete>
              <v-textarea
                v-model="email.notes"
                :label="$t('objects.person.emails.notes')"
                auto-select-first
                outlined
                multiple
                clearable
              />
            </div>
            <v-card outlined class="d-flex mb-8" @click="person.emails.push(new PersonEmail())">
              <v-list-item style="height: 56px">
                <v-list-item-subtitle>
                  {{ $t("person.addEmail") }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-card>
            <div v-for="(phone, index) in person.phones" :key="index">
              <v-subheader>
                {{ $t("common.nouns.phone") }} #{{ index + 1 }}
                <v-spacer />
                <v-btn text x-small @click="person.phones.splice(index, 1)">
                  {{ $t("person.removePhone") }}
                </v-btn>
              </v-subheader>
              <v-text-field
                v-model="phone.phone"
                :label="$t('objects.person.phones.phone') + ' *'"
                :rules="phoneRule"
                outlined
              />
              <v-autocomplete
                v-model="phone.label"
                :items="Object.values(PersonLabelEnum)"
                :label="$t('objects.person.phones.label')"
                auto-select-first
                outlined
              >
                <template #item="{ item }">
                  {{ $t(`enums.PersonLabelEnum.${item}`) }}
                </template>
                <template #selection="{ item }"> {{ $t(`enums.PersonLabelEnum.${item}`) }} </template>s
              </v-autocomplete>
              <v-textarea
                v-model="phone.notes"
                :label="$t('objects.person.phones.notes')"
                auto-select-first
                outlined
                multiple
                clearable
              />
            </div>
            <v-card outlined class="d-flex mb-8" @click="person.phones.push(new PersonPhone())">
              <v-list-item style="height: 56px">
                <v-list-item-subtitle>
                  {{ $t("person.addPhone") }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-card>
            <div v-for="(address, index) in person.addresses" :key="index">
              <v-subheader>
                {{ $t("objects.address.address") }} #{{ index + 1 }}
                <v-spacer />
                <v-btn text x-small @click="person.addresses.splice(index, 1)">
                  {{ $t("person.removeAddress") }}
                </v-btn>
              </v-subheader>
              <v-text-field v-model="address.street" :label="$t('objects.address.street')" outlined />
              <v-text-field v-model="address.zip" :label="$t('objects.address.zip')" outlined />
              <v-text-field v-model="address.city" :label="$t('objects.address.city')" outlined />
              <v-text-field v-model="address.state" :label="$t('objects.address.state')" outlined />
              <v-autocomplete
                v-model="address.countryCode"
                :items="Object.values(CountryCodeEnum)"
                :label="$t('objects.address.countryCode')"
                auto-select-first
                outlined
                :filter="getAutocompleteFilter((e) => [$t(`CountryCodeEnum.${e}`), e])"
              >
                <template #item="{ item }">
                  {{ $t(`CountryCodeEnum.${item}`) }}
                </template>
                <template #selection="{ item }">
                  {{ $t(`CountryCodeEnum.${item}`) }}
                </template>
              </v-autocomplete>
              <custom-field-boolean
                v-model="address.isBilling"
                :customField="{ label: $t('objects.person.addresses.isBilling') }"
              />
            </div>

            <v-card outlined class="d-flex mb-8" @click="person.addresses.push(new CompanyAddress())">
              <v-list-item style="height: 56px">
                <v-list-item-subtitle>
                  {{ $t("person.addAddress") }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-card>

            <v-autocomplete
              v-model="person.groupId"
              item-value="id"
              :items="groups"
              :label="$t('objects.person.groupId')"
              auto-select-first
              outlined
              clearable
              :loading="isLoadingPersonGroups"
              @click="refreshPersonGroups"
            >
              <template #item="{ item }">
                {{ item.title }}
              </template>
              <template #selection="{ item }">
                {{ item.title }}
              </template>
            </v-autocomplete>
            <div style="display: flex" class="mt-n7">
              <v-spacer />
              <v-btn x-small text :elevation="0" :loading="isLoadingPersonGroups" @click="refreshPersonGroups">
                {{ $t("common.verbs.refresh") }}
              </v-btn>
              <v-btn x-small text :elevation="0" @click="goToPersonGroupCustomView">
                {{ $t("common.verbs.view") }}
              </v-btn>
            </div>
            <div
              v-if="
                personGroup &&
                personGroup.configuration &&
                personGroup.configuration.customFieldConfig &&
                personGroup.configuration.customFieldConfig.length
              "
            >
              <v-progress-linear v-if="isLoadingCustomFieldValues" indeterminate />
              <!-- List of custom fields defined by project and filled out in the group -->
              <custom-field-list-form
                class="mt-6"
                v-model="person.values"
                :customFieldConfig="personGroup.configuration.customFieldConfig"
              />
              <debug>
                person.values: {{ person.values }}<br />
                person.groupId: {{ person.groupId }}<br />
                personGroup: {{ personGroup }}<br />
              </debug>
            </div>
          </v-col>
        </v-row>
        <debug>
          {{ person }}
        </debug>
      </v-form>
    </confirm-action-dialog>
  </div>
</template>

<script lang="ts">
import LatestEntriesCardEmpty from "@/components/cards/LatestEntriesCardEmpty.vue";
import CustomFieldListForm from "@/components/report/CustomFieldListForm.vue";
import Debug from "@/components/utility/Debug.vue";
import PaginatedTable from "@/components/utility/v2/PaginatedTable.vue";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";
import { PersonLabelEnum } from "@/lib/enum/person-label.enum";
import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { phoneRule, emailRule } from "@/lib/rules/contactRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { PersonGoToHelper } from "@/lib/utility/person.go-to-helper";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import PartnerFallbackMixin from "@/mixins/PartnerFallbackMixin.vue";
import { Address } from "@/models/address.entity";
import { Person, IPerson } from "@/models/person.entity";
import { ConfigModule } from "@/store/modules/config";
import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import RefsPersonGroup from "../utility/RefsPersonGroup.vue";
import CustomFieldBoolean from "../report/CustomFieldBoolean.vue";
import { CompanyAddress } from "@/models/company-address.entity";
import { IPersonGroup, PersonGroup } from "@/models/person-group.entity";
import { PersonGroupModule } from "@/store/modules/person-group.store";
import { PersonEmail } from "@/models/person-email.entity";
import { PersonPhone } from "@/models/person-phone.entity";
import { IReference } from "@/models/reference.entity";
import { PageFilterElement } from "@/models/page-filter-element.entity";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import MarkdownEditor from "@/components/utility/MarkdownEditor.vue";
import { getAutocompleteFilter } from "@/lib/utility/filter";

@Component({
  components: {
    PaginatedTable,
    ConfirmActionDialog,
    RefsPersonGroup,
    CustomFieldListForm,
    LatestEntriesCardEmpty,
    Debug,
    CustomFieldBoolean,
    MarkdownEditor
  },
  filters: {
    simpleDoubleDigitDate,
    getFlagEmojiByLanguage
  }
})
export default class PersonCreateDialog extends mixins(PartnerFallbackMixin) implements IDialog {
  readonly PersonLabelEnum = PersonLabelEnum;
  readonly Person = Person;
  readonly Address = Address;
  readonly CountryCodeEnum = CountryCodeEnum;
  readonly CompanyAddress = CompanyAddress;
  readonly PersonEmail = PersonEmail;
  readonly PersonPhone = PersonPhone;
  readonly getAutocompleteFilter = getAutocompleteFilter;

  @Prop()
  partnerId!: string;

  @Prop()
  hideButton!: boolean;

  /**
   * ID of person group to preselect
   */
  @Prop()
  groupId?: string;

  @Prop({ default: false })
  fullscreen?: boolean;

  @Prop({ default: () => [] })
  refs!: IReference[];

  @Prop({ default: "" })
  companyId!: string;

  @Prop({ default: false })
  isDialogActive!: boolean;

  get dialogModel() {
    return this.isDialogActive;
  }

  set dialogModel(v: boolean) {
    if (!v) {
      this.$emit("close");
    }
    this.$emit("update:isDialogActive", v);
  }

  isLoading = false;

  isFormValid = false;

  person: IPerson = new Person({
    groupId: this.groupId,
    partnerId: this.partnerId,
    addresses: [],
    emails: [],
    phones: [],
    refs: this.refs
  });

  personGroup: IPersonGroup | null = null;

  isLoadingCustomFieldValues = false;

  isLoadingPersonGroups = false;

  get groups() {
    return PersonGroupModule.entities;
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get emailRule() {
    return [emailRule()];
  }

  get phoneRule() {
    return [phoneRule()];
  }

  get availableLanguages() {
    return ConfigModule.availableLanguages;
  }

  open() {
    this.person = new Person({
      groupId: this.groupId,
      partnerId: this.partnerId,
      addresses: [],
      emails: [],
      phones: [],
      refs: this.refs
    });
    this.setPersonGroup();
    this.dialogModel = true;
    this.$nextTick(() => {
      (this.$refs.personNameField as HTMLInputElement)?.focus();
    });
  }

  close() {
    this.dialogModel = false;
  }

  async onRightClick() {
    try {
      this.isLoading = true;
      this.dialogModel = true;

      if (this.companyId) {
        if (!this.person.companyIds) {
          this.person.companyIds = [];
        }
        if (!this.person.companyIds.includes(this.companyId)) {
          this.person.companyIds.push(this.companyId);
        }
      }

      await this.person.create();
      this.$emit("created", this.person);
    } catch (e) {
      handleError(e);
    } finally {
      this.close();
      this.isLoading = false;
    }
  }

  addAddress() {
    if (!this.person.addresses) {
      this.person.addresses = [];
    }

    this.person.addresses.push(new CompanyAddress());
  }

  popAddress(index: number) {
    if (!this.person.addresses || this.person.addresses.length < 1) {
      return;
    }

    this.person.addresses.splice(index, 1);
  }

  @Watch("person.groupId")
  async setPersonGroup() {
    this.personGroup = null;

    const personGroupId = this.person.groupId;
    if (personGroupId) {
      this.isLoadingCustomFieldValues = true;
      const personGroup =
        PersonGroupModule.maps.id.get(personGroupId)[0] ||
        (await new PersonGroup({ partnerId: this.person.partnerId, id: personGroupId }).fetch().catch(handleError));
      this.isLoadingCustomFieldValues = false;

      this.$nextTick(() => {
        this.personGroup = personGroup;
      });
    }
  }

  goToPersonGroupCustomView() {
    if (!this.person.groupId) return;

    new PersonGoToHelper(this.$router).goToPersonGroupCustomView({
      partnerId: this.partnerId,
      personGroupId: this.person.groupId,
      viewId: "0",
      newTab: true
    });
  }

  async refreshPersonGroups() {
    this.isLoadingPersonGroups = true;
    try {
      PersonGroupModule.setFilters([
        new PageFilterElement({
          key: "type",
          operation: PageFilterOperationEnum.EQUAL,
          value: ResourceEnum.PERSON
        })
      ]);
      await PersonGroupModule.fetchFirstPage({ partnerId: this.partnerId });
    } catch (e) {
      handleError(e);
    }
    this.isLoadingPersonGroups = false;
  }
}
</script>
